import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SearchMessage } from 'features/AISearch/types';
import { removeTags } from 'utils/highlighting';

interface SearchSessionState {
  lastQuery: string;
  isSendingMessage: boolean;
  isLoadingFilters: boolean;
  messages: SearchMessage[];
  lastMessage: SearchMessage | null;
  historyPage: number;
}

const initialState: SearchSessionState = {
  lastQuery: '',
  isSendingMessage: false,
  isLoadingFilters: false,
  messages: [],
  lastMessage: null,
  historyPage: 0,
};

const searchSessionSlice = createSlice({
  name: 'searchSession',
  initialState,
  reducers: {
    resetSearchSession: () => initialState,
    setLastQuery: (state, action: PayloadAction<string>) => {
      state.lastQuery = removeTags(action.payload);
    },
    setSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.isSendingMessage = action.payload;
    },
    setLoadingFilters: (state, action: PayloadAction<boolean>) => {
      state.isLoadingFilters = action.payload;
    },
    setMessages: (state, action: PayloadAction<SearchMessage[]>) => {
      const messages = action.payload
        .map(message => {
          const { userQuery } = message;
          const newMessage: SearchMessage = {
            ...message,
            userQuery: userQuery ? removeTags(userQuery) : undefined,
          };

          return newMessage;
        })
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      state.messages = messages;

      if (action.payload.length > 0) {
        const { userQuery } = messages[messages.length - 1];
        const newMessage: SearchMessage = {
          ...messages[messages.length - 1],
          userQuery: userQuery ? removeTags(userQuery) : undefined,
        };
        state.lastMessage = newMessage;
      } else {
        state.lastMessage = null;
      }
    },
    addMessage: (state, action: PayloadAction<SearchMessage>) => {
      const { userQuery } = action.payload;
      const message: SearchMessage = {
        ...action.payload,
        userQuery: userQuery ? removeTags(userQuery) : undefined,
      };
      state.messages = [...state.messages, message].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      state.lastMessage = message;
    },
    updateLastMessage: (state, action: PayloadAction<SearchMessage>) => {
      const { userQuery } = action.payload;
      const message: SearchMessage = {
        ...action.payload,
        userQuery: userQuery ? removeTags(userQuery) : undefined,
      };
      state.messages = [...state.messages.slice(0, -1), message];
      state.lastMessage = message;
    },
    setHistoryPage: (state, action: PayloadAction<number>) => {
      state.historyPage = action.payload;
    },
  },
});

export const {
  resetSearchSession,
  setLastQuery,
  setSendingMessage,
  setMessages,
  addMessage,
  setLoadingFilters,
  updateLastMessage,
  setHistoryPage,
} = searchSessionSlice.actions;

export default searchSessionSlice.reducer;
